import { format, parseISO } from 'date-fns';
import type { PageView, VisitorSession } from '../../lib/analytics';

interface Props {
  recentViews: PageView[];
  recentSessions: VisitorSession[];
}

function formatLocation(s: VisitorSession): string {
  const parts = [s.city, s.region, s.country].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : '';
}

export default function RecentActivity({ recentViews, recentSessions }: Props) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Recent Page Views</h3>
        <div className="divide-y divide-gray-50">
          {recentViews.length === 0 ? (
            <p className="text-sm text-gray-400 py-4 text-center">No data yet</p>
          ) : (
            recentViews.slice(0, 10).map(pv => (
              <div key={pv.id} className="py-2.5 flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-mono text-gray-700 truncate max-w-[200px]">{pv.path}</p>
                  <p className="text-xs text-gray-400">
                    {pv.browser}{pv.browser_version ? ` ${pv.browser_version}` : ''} &middot; {pv.os}{pv.os_version ? ` ${pv.os_version}` : ''} &middot; {pv.device_type}
                  </p>
                  {(pv.device_vendor || pv.device_model) && (
                    <p className="text-xs text-gray-400">
                      {[pv.device_vendor, pv.device_model].filter(Boolean).join(' ')}
                    </p>
                  )}
                  {pv.ip_address && (
                    <p className="text-xs text-gray-300 font-mono">{pv.ip_address}</p>
                  )}
                </div>
                <span className="text-xs text-gray-400 flex-shrink-0">
                  {format(parseISO(pv.created_at), 'MMM d, HH:mm')}
                </span>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Recent Sessions</h3>
        <div className="divide-y divide-gray-50">
          {recentSessions.length === 0 ? (
            <p className="text-sm text-gray-400 py-4 text-center">No data yet</p>
          ) : (
            recentSessions.slice(0, 10).map(s => (
              <div key={s.id} className="py-2.5 flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm text-gray-700 truncate">
                    {s.browser}{s.browser_version ? ` ${s.browser_version}` : ''} / {s.os}{s.os_version ? ` ${s.os_version}` : ''}
                    {s.is_returning && (
                      <span className="ml-2 text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">returning</span>
                    )}
                  </p>
                  <p className="text-xs text-gray-400">
                    {s.device_type}
                    {(s.device_vendor || s.device_model) && (
                      <> &middot; {[s.device_vendor, s.device_model].filter(Boolean).join(' ')}</>
                    )}
                    &middot; {s.page_count} page{s.page_count !== 1 ? 's' : ''} &middot; from {s.entry_path ?? '/'}
                  </p>
                  {formatLocation(s) && (
                    <p className="text-xs text-gray-400">{formatLocation(s)}{s.timezone ? ` (${s.timezone})` : ''}</p>
                  )}
                  {s.ip_address && (
                    <p className="text-xs text-gray-300 font-mono">{s.ip_address}</p>
                  )}
                </div>
                <span className="text-xs text-gray-400 flex-shrink-0">
                  {format(parseISO(s.started_at), 'MMM d, HH:mm')}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
